/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */

import { compact, filter, find, first, flatMap, map, minBy, sortBy, orderBy, last } from 'lodash';
import { truncateByMinute } from 'blb-utils/date';
import { ECloseAuditEventNames } from 'blb-persona/common/constants';
const getCreatedAtMs = _ref => {
  let {
    createdAt
  } = _ref;
  return new Date(createdAt).valueOf();
};

/**
 * On every PRE_CLOSING_DOCUMENTS addition, we change PRE_CLOSING designation to
 * DRAFT_PRE_CLOSING designation, and then on pre-closing-documents-send we convert
 * them back to PRE_CLOSING designation, which means all the documents are a single
 * set of PRE_CLOSING documents. So, we can simply group them together into a single document with
 * the timestamp from latest PRE_CLOSING_DOCUMENTS_SENT audit event.
 *
 * For other documents with PRE_CLOSING designation such as TRAILING_DOCUMENTS or
 * ADDITIONAL_BORROWER_DOCUMENTS, we do not record any Audit Event sent events,
 * they are not uploaded frequently but are uploaded mostly by settlement_agents.
 */
export const groupPreClosingDesignationDocuments = (auditEvents, preClosingdocuments) => {
  if (preClosingdocuments.length === 0) {
    return [];
  }
  const sortedDocs = sortBy(preClosingdocuments, 'createdAt');
  const preClosingDocumentsSendEvents = filter(auditEvents, {
    type: ECloseAuditEventNames.PRE_CLOSING_DOCUMENTS_SENT
  });
  const sortedEvents = sortBy(preClosingDocumentsSendEvents, getCreatedAtMs);
  let eventIndex = -1;
  const documentWithTags = sortedDocs.map(doc => {
    // find the next PRE_CLOSING_DOCUMENTS_SENT that has createdAt > doc.createdAt
    while (eventIndex < 0 || eventIndex < sortedEvents.length && getCreatedAtMs(sortedEvents[eventIndex]) <= doc.createdAt) {
      ++eventIndex;
    }
    const sentOn = eventIndex >= sortedEvents.length ?
    // When there is PRE_CLOSING docs, we expect to have at least one event PRE_CLOSING_DOCUMENTS_SENT
    // This fall back is just for safety.
    last(sortedDocs).createdAt : getCreatedAtMs(sortedEvents[eventIndex]);
    return {
      ...doc,
      // when true, show label "ADD" closed to doc
      sentInMostRecentBatch: eventIndex >= sortedEvents.length - 1,
      sentOn
    };
  });
  return orderBy(documentWithTags, 'createdAt', 'desc');
};
export const getSortedDocsWithTagByMinute = documents => {
  const sortedDocs = sortBy(documents, _ref2 => {
    let {
      createdAt
    } = _ref2;
    return createdAt * -1;
  });
  return map(sortedDocs, doc => ({
    ...doc,
    sentInMostRecentBatch: false,
    sentOn: truncateByMinute(doc.createdAt)
  }));
};
const sentStatuses = ['SENT', 'SENT_DAY_OF'];
const isSent = status => sentStatuses.includes(status);
export const groupDocumentsByRedrawWindows = (auditEvents, documents) => {
  const redraws = filter(auditEvents, event => event.type === 'CLOSING_STATUS_CHANGED' && isSent(event.meta?.from) && event.meta?.to === 'DRAFT');
  const sends = filter(auditEvents, event => {
    return event.type === 'CLOSING_STATUS_CHANGED' && event.meta?.from === 'DRAFT' && isSent(event.meta?.to);
  });
  const firstRedrawWindow = {
    end: redraws.length ? getCreatedAtMs(minBy(redraws, redraw => redraw.createdAt)) : Number.POSITIVE_INFINITY,
    isInitialWindow: true,
    sentOn: sends.length ? getCreatedAtMs(minBy(sends, send => send.createdAt)) : Number.POSITIVE_INFINITY,
    start: Number.NEGATIVE_INFINITY
  };
  const redrawWindows = compact(map(redraws, currentRedraw => {
    const currentRedrawTimestamp = getCreatedAtMs(currentRedraw);
    const subsequentRedraws = filter(redraws, redraw => getCreatedAtMs(redraw) > getCreatedAtMs(currentRedraw));
    const end = subsequentRedraws.length ? getCreatedAtMs(minBy(subsequentRedraws, redraw => redraw.createdAt)) : Number.POSITIVE_INFINITY;
    const sendInWindow = find(sends, send => {
      const sentTimestamp = getCreatedAtMs(send);
      return sentTimestamp > currentRedrawTimestamp && sentTimestamp < end;
    });
    if (!sendInWindow) return undefined;
    return {
      end,
      isInitialWindow: false,
      sentOn: getCreatedAtMs(sendInWindow),
      start: getCreatedAtMs(currentRedraw)
    };
  }));
  const allWindows = [firstRedrawWindow, ...redrawWindows];
  const sortedWindows = sortBy(allWindows, window => -1 * window.start);
  const mostRecentWindowStart = first(sortedWindows).start;
  const windowsWithDocs = map(sortedWindows, window => {
    const docsInWindow = filter(documents, doc => doc.createdAt > window.start && doc.createdAt < window.end);
    return {
      ...window,
      docsInWindow: map(docsInWindow, doc => ({
        ...doc,
        sentInMostRecentBatch: !window.isInitialWindow && mostRecentWindowStart === window.start,
        sentOn: window.sentOn
      }))
    };
  });
  return flatMap(windowsWithDocs, window => window.docsInWindow);
};